/**
 * Defines all Drimify Tokens.
 *
 * This object serves as the authoritative source for Drimify-specific design tokens in JavaScript.
 * These tokens define various styling properties such as colors, borders, text alignment, and more.
 *
 * **Important:** This is the only place where Drimify Tokens should be listed in JavaScript.
 * Utility methods are used to generate the corresponding TypeScript interface.
 *
 * Only the keys are ever read (the values are placeholders). They are `null` rather than
 * `undefined` because type tooling that parses this literal (the JSON Schema generator in
 * tools/json-schema) can represent a null literal but not the `undefined` identifier.
 */
export const DSCustomTokenKeys = {
  /**
   * Represents the CSS color value used to highlight a specific user in the leaderboard.
   * This value should be a valid CSS hex color value or point to a Material Design System variable.
   */
  '--ds-leaderboard-cell-user-highlight': null,

  /**
   * Represents the CSS color value used to indicate changes within a leaderboard cell.
   * This value should be a valid CSS hex color value or point to a Material Design System variable.
   */
  '--ds-leaderboard-cell-change-highlight': null,

  /**
   * Represents the CSS color value used for the border of a dynamic path node.
   * This value determines the border color of nodes in a dynamic path visualization.
   */
  '--ds-dynamic-path-node-border': null,

  /**
   * Represents the CSS color value used for SVG lines in a dynamic path visualization.
   * This value determines the color of the connecting lines.
   */
  '--ds-dynamic-path-svg-line': null,

  /**
   * Represents the CSS border-radius value applied to elements.
   * This defines the curvature of corners for elements that use this variable.
   */
  '--ds-radius': null,

  /**
   * Represents the CSS alignment value for large headlines.
   * This value determines the text alignment (e.g., left, center, right) of large headlines.
   */
  '--ds-headline-large-align': null,

  /**
   * Represents the CSS color value for large headlines.
   * This value should be a valid CSS hex color value or point to a Material Design System variable.
   */
  '--ds-headline-large-color': null,

  /**
   * Represents the CSS alignment value for medium-sized body text.
   * This value determines the text alignment (e.g., left, center, right) for medium-sized body content.
   */
  '--ds-body-medium-align': null,

  /**
   * Represents the CSS color value for medium-sized body text.
   * This value should be a valid CSS hex color value or point to a Material Design System variable.
   */
  '--ds-body-medium-color': null,

  ////////////////////////////////////////////////////////////////////////////////////////////////////////
  // BUTTON TOKENS
  ////////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Represents the CSS color value used for button backgrounds.
   * This value should be a valid CSS hex color value or point to a Material Design System variable.
   */
  '--ds-button-background-color': null,

  /**
   * Represents the CSS color value used for button labels.
   * This value should be a valid CSS hex color value or point to a Material Design System variable.
   */
  '--ds-button-label-color': null,

  /**
   * Represents the CSS font-weight value for button labels.
   * This value determines the font weight (e.g., bold, normal) of button labels.
   * Note that your loaded font must support this weight.
   */
  '--ds-button-label-weight': null,

  /**
   * Represents the CSS URL value for button textures.
   * This value specifies the URL of the texture or background image used for buttons.
   */
  '--ds-button-background-image-url': null,

  ////////////////////////////////////////////////////////////////////////////////////////////////////////
  // PLUGIN TOKENS
  ////////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Represents the CSS color value used for the font color in plugin text.
   * This value should be a valid CSS hex color value or point to a Material Design System variable.
   */
  '--ds-plugin-font-color': null,

  /**
   * Represents the CSS font-family value used for plugin text.
   * This value should be a valid CSS font-family value or point to a Material Design System variable.
   */
  '--ds-plugin-font-family': null,

  /**
   * Represents the CSS font-weight value used for plugin text.
   * This value should be a valid CSS font-weight value or point to a Material Design System variable.
   */
  '--ds-plugin-font-weight': null,

  /**
   * Represents the CSS font-size value used for plugin text.
   * This value should be a valid CSS font-size value or point to a Material Design System variable.
   */
  '--ds-plugin-font-size': null,

  /**
   * Represents the CSS line-height value used for plugin text.
   * This value should be a valid CSS line-height value or point to a Material Design System variable.
   */
  '--ds-plugin-font-line-height': null,

  /**
   * Represents the CSS stroke width value used for plugin text.
   * This value should be a valid CSS stroke width value.
   */
  '--ds-plugin-font-stroke-width': null,

  /**
   * Represents the CSS color value used for the stroke of plugin text.
   * This value should be a valid CSS hex color value or point to a Material Design System variable.
   */
  '--ds-plugin-font-stroke-color': null,

  ////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ELEMENT TEXT TOKENS
  //
  // Theme the text rendered by custom elements (the Lit components hosted by `app-element`,
  // e.g. animated text).
  //
  // ONE RULE FOR THE WHOLE GROUP: every token DELIBERATELY has no default, and an unset
  // token is not written to `:root` at all, so the element bridge (element.component.scss)
  // passes nothing through and the hosted element's own built-in default applies. There is
  // no per-token special case and nothing here piggybacks on another group.
  //
  // The element ships those defaults because it is the thing doing the rendering: its values
  // are proportional and scale with their container, where the `--ds-plugin-font-*` values
  // are fixed pixel sizes designed for plugin text. See the token table in
  // `@digitaservice/component-ui` (src/shared/tokens.ts) for the exact defaults, which move
  // with the element version rather than with this file.
  //
  // Names map 1:1 onto the element's own input API: `--ds-element-text-<x>` is bridged to
  // `--ds-theme-text-<x>` with no renaming in between.
  //
  // A LOOK IS A COMBINATION, NOT A NAME. There is no "style" field — the back office produces
  // each of the four signed-off looks by sending a set of these values. Out of the box the
  // element renders the outline look, so an unthemed game stays legible over arbitrary stage
  // content without anyone configuring anything; the other three looks each have to switch the
  // outline off first (`stroke-width: 0`, plus `glyph-shadow: none` for the two box looks).
  ////////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Represents the CSS color value used for the fill of element text.
   * This value should be a valid CSS hex color value or point to a Material Design System variable.
   */
  '--ds-element-text-color': null,

  /**
   * Represents the CSS font-family value used for element text.
   * This value should be a valid CSS font-family value or point to a Material Design System variable.
   */
  '--ds-element-text-font-family': null,

  /**
   * Represents the CSS font-weight value used for element text.
   * This value should be a valid CSS font-weight value or point to a Material Design System variable.
   *
   * The loaded font must actually ship the weight. The element sets `font-synthesis: none`, so
   * a weight the font does not contain renders at the nearest real one rather than being faked —
   * a display face shipping a single weight (Anton, for one) ignores this token entirely.
   */
  '--ds-element-text-font-weight': null,

  /**
   * Represents the CSS font-size value used for element text.
   * This value should be a valid CSS font-size value or point to a Material Design System variable.
   *
   * Setting this opts out of the proportional sizing the element does by default, which scales
   * the text to its container. A fixed size will not adapt between a phone and a desktop embed.
   */
  '--ds-element-text-font-size': null,

  /**
   * Represents the CSS line-height value used for element text.
   * This value should be a valid CSS line-height value or point to a Material Design System variable.
   *
   * This is the leading between wrapped lines. For the tighter leading a box look needs, see
   * `--ds-element-text-line-leading`.
   */
  '--ds-element-text-line-height': null,

  /**
   * Represents the CSS color value used for the effect color of element text.
   * This value should be a valid CSS hex color value or point to a Material Design System variable.
   *
   * Despite the name this is the one effect color for the whole component: the outline stroke,
   * the glyph shadow and the box are all derived from it, so a non-black choice carries through
   * every look instead of going inert. Set this rather than repeating a color per look.
   */
  '--ds-element-text-stroke-color': null,

  /**
   * Represents the CSS stroke width value used for the outline on element text.
   * This value should be a valid CSS length value.
   *
   * A fixed length, so the outline stays a constant thickness at any viewport or font size.
   * `0` switches the outline off, which the three non-default looks must do first.
   */
  '--ds-element-text-stroke-width': null,

  /**
   * Represents the CSS color value used for the box drawn behind element text.
   * This value should be a valid CSS hex color value or point to a Material Design System variable.
   *
   * Unset means no box. The box opacity is the alpha of this color — there is no separate
   * opacity token.
   */
  '--ds-element-text-background-color': null,

  /**
   * Represents the CSS border-radius value applied to the box behind element text.
   * This value should be a valid CSS border-radius value.
   *
   * This is the only difference between the rounded rectangle and rectangle looks.
   */
  '--ds-element-text-background-radius': null,

  /**
   * Represents the CSS padding value applied to the box behind element text.
   * This value should be a valid CSS padding value.
   *
   * How tightly the box hugs its text. Use `em` units so it tracks the font size.
   */
  '--ds-element-text-background-padding': null,

  /**
   * Represents the CSS box-shadow value applied to the box behind element text.
   * This value should be a valid CSS box-shadow value.
   *
   * Only meaningful alongside a background color: a box shadow paints even where the box itself
   * is transparent, which is why this is off unless a look asks for it.
   */
  '--ds-element-text-background-shadow': null,

  /**
   * Represents the CSS text-shadow value applied to the letters of element text.
   * This value should be a valid CSS text-shadow value.
   *
   * Unset is the shadow that completes the default outline look, derived from
   * `--ds-element-text-stroke-color`. `none` switches it off, which both box looks must do.
   */
  '--ds-element-text-glyph-shadow': null,

  /**
   * Represents the CSS gap value between the stacked lines of element text.
   * This value should be a valid CSS length value.
   *
   * Separates the boxes of a multi-line box look, which would otherwise touch. Use `em` units
   * so it tracks the font size.
   */
  '--ds-element-text-line-gap': null,

  /**
   * Represents the CSS align-items value used for the lines of element text.
   * Valid values are `center` and `stretch`.
   *
   * Applies in landscape only. `stretch` gives the box looks full-width bands; `center` makes
   * each box hug its own line.
   */
  '--ds-element-text-line-align': null,

  /**
   * Represents the CSS line-height value used inside a single line of element text.
   * This value should be a valid CSS line-height value.
   *
   * Distinct from `--ds-element-text-line-height`, which spaces wrapped lines apart. A box is
   * drawn to the line box rather than to the glyphs, so text-sized leading makes stacked boxes
   * tall enough to merge into one dark mass. Unset, the element reuses the line height above.
   */
  '--ds-element-text-line-leading': null,
} as const;

/**
 * Default Drimify Tokens.
 *
 * This object provides the default values for Drimify-specific design tokens.
 * These values ensure consistency across the Drimify platform while allowing customization.
 *
 * Some tokens reference Material Design System variables using CSS `var()` syntax.
 */
export const DefaultDSCustomTokens: DSCustomTokens = {
  // The M3 "selected item" container: a light tint in light theme, a muted one
  // in dark. `inverse-primary` (the previous default) read too heavy for a row
  // highlight; a faint tint (rather than a plain neutral surface) keeps the
  // current-user row visible even when a board sits on a neutral panel.
  '--ds-leaderboard-cell-user-highlight': 'var(--mat-sys-secondary-container)',
  '--ds-leaderboard-cell-change-highlight': 'var(--mat-sys-surface-container-lowest)',
  '--ds-dynamic-path-node-border': 'var(--mat-sys-outline)',
  '--ds-dynamic-path-svg-line': 'var(--mat-sys-outline)',
  '--ds-radius': '10px',
  '--ds-headline-large-align': 'center',
  '--ds-headline-large-color': 'var(--mat-sys-on-background)',
  '--ds-body-medium-align': 'center',
  '--ds-body-medium-color': 'var(--mat-sys-on-background)',
  // button tokens
  '--ds-button-background-color': 'var(--mat-sys-primary)',
  '--ds-button-label-color': 'var(--mat-sys-on-primary)',
  '--ds-button-label-weight': '700',
  '--ds-button-background-image-url': 'none',
  // plugin tokens
  '--ds-plugin-font-color': '#ffffff',
  '--ds-plugin-font-family': 'var(--mat-sys-display-small-font)',
  '--ds-plugin-font-weight': '900',
  '--ds-plugin-font-size': '40px',
  '--ds-plugin-font-line-height': '1.5',
  '--ds-plugin-font-stroke-width': '1px',
  '--ds-plugin-font-stroke-color': '#000000',
  // element text tokens intentionally have NO defaults: an unset token is never written
  // to :root, so the bridge passes nothing through and the hosted element renders its own
  // built-in default (see the ELEMENT TEXT TOKENS section of DSCustomTokenKeys).
};

/**
 * Generates Drimify Tokens.
 *
 * This function takes an optional `DSCustomTokens` object and processes the `--ds-button-background-image-url`
 * value to ensure it is formatted correctly.
 *
 * @param custom - An object containing custom Drimify tokens.
 *
 * @returns The generated Drimify tokens, or `undefined` if no custom tokens are provided.
 */
export const GenerateDSCustomTokens = (custom?: DSCustomTokens) => {
  // if there is no custom, return the default
  if (!custom) {
    return undefined;
  }

  return {
    ...custom,
    '--ds-button-background-image-url': processButtonTextureUrl(custom['--ds-button-background-image-url']),
  };
};

/**
 * Ensures `--ds-button-background-image-url` has a valid value.
 *
 * This function validates and processes the `--ds-button-background-image-url` token.
 * If the value is `undefined`, `null`, or empty, it defaults to `'none'`.
 * If the value is not already wrapped in `url()`, it wraps it accordingly.
 *
 * @param value - The button texture URL to process.
 *
 * @returns The validated and formatted button texture URL.
 */
const processButtonTextureUrl = (value?: string) => {
  // Default to 'none' if undefined, null, or empty
  if (!value || value === 'none') {
    return 'none';
  }
  // Check if already wrapped in `url()`
  if (value.startsWith('url(')) {
    // Use as-is if already wrapped
    return value;
  }
  // Otherwise, wrap in `url()` if missing
  return `url(${value})`;
};

/**
 * Utility Type: Maps keys from a constant to a TypeScript type.
 *
 * This helper type dynamically maps keys from a given object type `T` to a new type
 * where each key is optional and its value is a `string`.
 */
type MappedInterface<T> = {
  -readonly [K in keyof T]?: string;
};

/**
 * Generated Interface for Drimify Tokens.
 *
 * This interface represents the generated Drimify tokens, extending the `DSCustomTokenKeys`
 * structure while allowing optional properties. This ensures flexibility in defining custom tokens.
 */
export interface DSCustomTokens extends Partial<MappedInterface<typeof DSCustomTokenKeys>> {}
